import React, { useState, useEffect } from "react";
import { Typography, Box } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

interface CountdownTimerProps {
  expiresAt: string;
  onExpire?: () => void;
  showIcon?: boolean;
}

const CountdownTimer: React.FC<CountdownTimerProps> = ({
  expiresAt,
  onExpire,
  showIcon = true,
}) => {
  const [timeLeft, setTimeLeft] = useState<string>("");
  const [isExpired, setIsExpired] = useState(false);
  const [isUrgent, setIsUrgent] = useState(false);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const expiry = new Date(expiresAt).getTime();
      const difference = expiry - now;

      if (difference <= 0) {
        setIsExpired(true);
        setTimeLeft("Expired");
        if (onExpire) {
          onExpire();
        }
        return;
      }

      // Mark as urgent if less than 1 hour remaining
      setIsUrgent(difference < 60 * 60 * 1000);

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
      );
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      if (days > 0) {
        setTimeLeft(`${days}d ${hours}h ${minutes}m`);
      } else if (hours > 0) {
        setTimeLeft(`${hours}h ${minutes}m ${seconds}s`);
      } else if (minutes > 0) {
        setTimeLeft(`${minutes}m ${seconds}s`);
      } else {
        setTimeLeft(`${seconds}s`);
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [expiresAt, onExpire]);

  const getColor = () => {
    if (isExpired) return "text.disabled";
    if (isUrgent) return "error.main";
    return "text.secondary";
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      {showIcon && (
        <AccessTimeIcon
          sx={{
            fontSize: 14,
            color: getColor(),
          }}
        />
      )}
      <Typography
        variant="caption"
        sx={{
          color: getColor(),
          fontWeight: isUrgent ? 600 : 400,
          fontFamily: "monospace",
        }}
      >
        {timeLeft}
      </Typography>
    </Box>
  );
};

export default CountdownTimer;
